// 새로운 크루 API 서비스
// 기존 복잡한 로직을 단순화하여 안정성과 성능 향상

// Normalize API base URL and remove any /api suffix to prevent double /api
let API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
API_URL = API_URL.replace(/\/$/, ''); // remove trailing slash
API_URL = API_URL.replace(/\/api$/, ''); // remove /api suffix if present

// 타입 정의
export interface Crew {
  id: string;
  name: string;
  description?: string;
  image?: string;
  color?: string;
  maxMembers?: number;
  isActive?: boolean;
  isOfficial: boolean;
  leaderId: string;
  createdAt: string;
  updatedAt?: string;
  leader: {
    id: string;
    name: string;
    avatar?: string;
  };
  memberCount: number;  // API 응답 형식에 맞춤
  todayCompletedCount?: number;
  progressPercent?: number; // 0-100
  myRole?: 'LEADER' | 'MEMBER';  // /my-crews 응답에 포함
  joinedAt?: string;  // /my-crews 응답에 포함
  members?: CrewMember[];  // 상세 조회 시에만 포함
  _count?: {  // 하위 호환성 유지
    members: number;
  };
}

export interface CrewMember {
  id: string;
  userId: string;
  crewId: string;
  role: 'LEADER' | 'MEMBER';
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  joinedAt: string;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    name: string;
    avatar?: string;
  };
}

export interface CrewJoinRequest {
  id: string;
  userId: string;
  crewId: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  message?: string;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    name: string;
    avatar?: string;
  };
}

export interface CreateCrewData {
  name: string;
  description?: string;
  image?: string;
  color?: string;
  maxMembers?: number;
}

export interface UpdateCrewData {
  name?: string;
  description?: string;
  image?: string;
  color?: string;
  maxMembers?: number;
}

// API 헬퍼 함수
const apiRequest = async (url: string, options: RequestInit = {}) => {
  const token = localStorage.getItem('accessToken');

  // Join URL safely without corrupting protocol (avoid replacing 'https://')
  const path = url.startsWith('/') ? url : `/${url}`;
  const fullUrl = `${API_URL}${path}`;
  const response = await fetch(fullUrl, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });

  if (!response.ok) {
    // Try parse JSON error, but fallback to text to avoid HTML parse crash
    const contentType = response.headers.get('content-type') || '';
    const errorData = contentType.includes('application/json')
      ? await response.json().catch(() => ({}))
      : { error: await response.text().catch(() => `HTTP ${response.status}`) };
    
    // 401 에러는 실제 인증 오류가 아닐 수 있으므로 특별 처리
    // 대신 사용자 친화적인 에러 메시지로 변환
    if (response.status === 401) {
      console.warn('⚠️ 크루 API 인증 오류 - 하지만 로그아웃하지 않음:', errorData.error);
      // 401 에러를 일반 에러로 변환하여 로그아웃 방지
      const error: any = new Error(errorData.error || '권한이 없습니다. 다시 시도해주세요.');
      error.status = 403; // 401 대신 403으로 변환하여 자동 로그아웃 방지
      error.isAuthError = false; // 자동 로그아웃을 트리거하지 않도록 플래그 추가
      throw error;
    }
    
    const error: any = new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    error.status = response.status;
    throw error;
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    const text = await response.text().catch(() => '');
    const error: any = new Error('Invalid JSON response');
    error.status = 502;
    error.body = text;
    throw error;
  }
  return response.json();
};

// ========================================
// 크루 목록 조회
// ========================================
export const getAvailableCrews = async (retry = 0): Promise<Crew[]> => {
  try {
    console.log('📋 크루 목록 조회 요청');
    const response = await apiRequest('/api/crews/available');
    const crews = response.data || [];
    console.log(`✅ 크루 목록 조회 완료: ${crews.length}개`);
    return crews;
  } catch (error: any) {
    // 429 재시도(지수 백오프)
    if ((error?.status === 429 || /Too Many Requests/i.test(String(error))) && retry < 3) {
      const delay = Math.pow(2, retry) * 1000;
      console.warn(`⚠️ 429 응답 - ${delay}ms 후 재시도 (${retry + 1}/3)`);
      await new Promise((r) => setTimeout(r, delay));
      return getAvailableCrews(retry + 1);
    }
    console.error('❌ 크루 목록 조회 오류:', error);
    throw error;
  }
};

// ========================================
// 내 크루 목록 조회
// ========================================
let myCrewsCache: Crew[] | null = null;
let myCrewsFetchedAt = 0;
let myCrewsInFlight: Promise<Crew[]> | null = null;

export const getMyCrews = async (retry = 0): Promise<Crew[]> => {
  const now = Date.now();
  if (myCrewsCache && now - myCrewsFetchedAt < 15000) {
    return myCrewsCache;
  }
  if (myCrewsInFlight) return myCrewsInFlight;

  myCrewsInFlight = (async () => {
    try {
      console.log('👤 내 크루 목록 조회 요청');
      const response = await apiRequest('/api/crews/my-crews');
      const crews = response.data || [];
      myCrewsCache = crews;
      myCrewsFetchedAt = Date.now();
      console.log(`✅ 내 크루 목록 조회 완료: ${crews.length}개`);
      return crews;
    } catch (error: any) {
      if ((error?.status === 429 || /Too Many Requests/i.test(String(error))) && retry < 3) {
        const delay = Math.pow(2, retry) * 1000;
        console.warn(`⚠️ my-crews 429 - ${delay}ms 후 재시도 (${retry + 1}/3)`);
        await new Promise((r) => setTimeout(r, delay));
        myCrewsInFlight = null;
        return getMyCrews(retry + 1);
      }
      console.error('❌ 내 크루 목록 조회 오류:', error);
      throw error;
    } finally {
      myCrewsInFlight = null;
    }
  })();

  return myCrewsInFlight;
};

// ========================================
// 내 대표 크루 조회 (호환용)
// ========================================
export const getMyCrew = async (): Promise<Crew | null> => {
  try {
    const crews = await getMyCrews();
    return crews.length > 0 ? crews[0] : null;
  } catch (error) {
    console.error('❌ 내 대표 크루 조회 오류:', error);
    return null;
  }
};

// ========================================
// 크루 상세 조회
// ========================================
export const getCrewById = async (id: string): Promise<Crew> => {
  try {
    console.log('🔍 크루 상세 조회 요청:', id);
    const response = await apiRequest(`/api/crews/${id}`);
    const crew = response.data || response; // 호환성 유지
    console.log(`✅ 크루 상세 조회 완료: ${crew.name}`);
    return crew;
  } catch (error) {
    console.error('❌ 크루 상세 조회 오류:', error);
    throw error;
  }
};

// ========================================
// 크루 생성
// ========================================
export const createCrew = async (data: CreateCrewData): Promise<Crew> => {
  try {
    console.log('➕ 크루 생성 요청:', data);
    const response = await apiRequest('/api/crews', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    const crew = response.data || response; // 호환성 유지
    console.log(`✅ 크루 생성 완료: ${crew.name}`);
    return crew;
  } catch (error) {
    console.error('❌ 크루 생성 오류:', error);
    throw error;
  }
};

// ========================================
// 크루 정보 수정
// ========================================
export const updateCrew = async (id: string, data: UpdateCrewData): Promise<Crew> => {
  try {
    console.log('✏️ 크루 정보 수정 요청:', { id, data });
    const response = await apiRequest(`/api/crews/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    const crew = response.data || response;
    console.log(`✅ 크루 정보 수정 완료: ${crew.name}`);
    return crew;
  } catch (error) {
    console.error('❌ 크루 정보 수정 오류:', error);
    throw error;
  }
};

// ========================================
// 크루 삭제
// ========================================
export const deleteCrew = async (id: string): Promise<void> => {
  try {
    console.log('🗑️ 크루 삭제 요청:', id);
    await apiRequest(`/api/crews/${id}`, {
      method: 'DELETE',
    });
    console.log('✅ 크루 삭제 완료');
  } catch (error) {
    console.error('❌ 크루 삭제 오류:', error);
    throw error;
  }
};

// ========================================
// 크루 가입 신청
// ========================================
export const joinCrew = async (id: string, message?: string): Promise<CrewJoinRequest> => {
  try {
    console.log('📝 크루 가입 신청 요청:', { id, message });
    const response = await apiRequest(`/api/crews/${id}/join`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
    const request = response.data || response;
    console.log('✅ 크루 가입 신청 완료');
    return request;
  } catch (error) {
    console.error('❌ 크루 가입 신청 오류:', error);
    throw error;
  }
};

// ========================================
// 크루 탈퇴
// ========================================
export const leaveCrew = async (id: string): Promise<void> => {
  try {
    console.log('👋 크루 탈퇴 요청:', id);
    await apiRequest(`/api/crews/${id}/leave`, {
      method: 'DELETE',
    });
    console.log('✅ 크루 탈퇴 완료');
  } catch (error) {
    console.error('❌ 크루 탈퇴 오류:', error);
    throw error;
  }
};

// ========================================
// 가입 신청 목록 조회
// ========================================
export const getJoinRequests = async (id: string): Promise<CrewJoinRequest[]> => {
  try {
    console.log('📋 가입 신청 목록 조회 요청:', id);
    const response = await apiRequest(`/api/crews/${id}/requests`);
    const requests = response.data || [];
    console.log(`✅ 가입 신청 목록 조회 완료: ${requests.length}개`);
    return requests;
  } catch (error) {
    console.error('❌ 가입 신청 목록 조회 오류:', error);
    throw error;
  }
};

// ========================================
// 특정 멤버의 오늘 묵상 상세 조회
// ========================================
export const getMemberMeditationToday = async (userId: string): Promise<{
  id: string;
  user: { id: string; name: string; avatar?: string };
  date: string;
  verseReference?: string;
  verseText?: string;
  readingCount?: number;
  godsWill?: string;
  growthChange?: string;
  prayer?: string | null;
  prayerShareType?: string;
  createdAt?: string;
}> => {
  try {
    console.log('🔍 멤버 오늘 묵상 상세 조회 요청:', userId);
    const response = await apiRequest(`/api/crews/member/${userId}/today`);
    console.log('✅ 멤버 오늘 묵상 상세 조회 완료');
    return response;
  } catch (error) {
    console.error('❌ 멤버 오늘 묵상 상세 조회 오류:', error);
    throw error;
  }
};

// ========================================
// 오늘의 크루 멤버 상태 조회
// ========================================
export const getMyCrewMembersToday = async (): Promise<any[]> => {
  try {
    console.log('📊 오늘의 크루 멤버 상태 조회 시작');
    const response = await apiRequest('/api/crews/my-members/today');
    console.log('✅ 크루 멤버 상태 조회 완료:', response);
    return response.members || response || [];
  } catch (error: any) {
    console.error('❌ 크루 멤버 상태 조회 실패:', error);
    console.error('에러 상세:', {
      message: error?.message,
      status: error?.status,
      stack: error?.stack
    });
    
    // 가입한 크루가 없는 경우는 정상 케이스
    if (error?.message?.includes('가입한 크루가 없습니다')) {
      console.log('ℹ️ 가입한 크루 없음 - 빈 배열 반환');
      return [];
    }
    
    // 500 에러 등 서버 에러의 경우 빈 배열 반환 (임시)
    console.warn('⚠️ 서버 에러로 빈 배열 반환');
    return [];
  }
};

// ========================================
// 가입 신청 승인/거부
// ========================================
export const processJoinRequest = async (
  crewId: string,
  requestId: string,
  status: 'APPROVED' | 'REJECTED'
): Promise<void> => {
  try {
    console.log('✅ 가입 신청 처리 요청:', { crewId, requestId, status });
    await apiRequest(`/api/crews/${crewId}/requests/${requestId}`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
    console.log(`✅ 가입 신청 처리 완료: ${status}`);
  } catch (error) {
    console.error('❌ 가입 신청 처리 오류:', error);
    throw error;
  }
};

// ========================================
// 크루장 권한 이관
// ========================================
export const transferLeadership = async (id: string, newLeaderId: string): Promise<void> => {
  try {
    console.log('👑 크루장 권한 이관 요청:', { id, newLeaderId });
    await apiRequest(`/api/crews/${id}/transfer-leadership`, {
      method: 'POST',
      body: JSON.stringify({ newLeaderId }),
    });
    console.log('✅ 크루장 권한 이관 완료');
  } catch (error) {
    console.error('❌ 크루장 권한 이관 오류:', error);
    throw error;
  }
};

// ========================================
// 멤버 강제 탈퇴
// ========================================
export const removeMember = async (crewId: string, memberId: string): Promise<void> => {
  try {
    console.log('👋 멤버 강제 탈퇴 요청:', { crewId, memberId });
    await apiRequest(`/api/crews/${crewId}/members/${memberId}`, {
      method: 'DELETE',
    });
    console.log('✅ 멤버 강제 탈퇴 완료');
  } catch (error) {
    console.error('❌ 멤버 강제 탈퇴 오류:', error);
    throw error;
  }
};

// ========================================
// 유틸리티 함수들
// ========================================

// 크루장 여부 확인
export const isCrewLeader = (crew: Crew, userId: string): boolean => {
  return crew.leaderId === userId;
};

// 크루 멤버 여부 확인
export const isCrewMember = (crew: Crew, userId: string): boolean => {
  return crew.members.some(member => 
    member.userId === userId && member.status === 'APPROVED'
  );
};

// 크루 멤버 역할 확인
export const getCrewMemberRole = (crew: Crew, userId: string): 'LEADER' | 'MEMBER' | null => {
  const member = crew.members.find(m => m.userId === userId && m.status === 'APPROVED');
  return member ? member.role : null;
};

// 크루 공유 링크 생성
export const getCrewShareLink = (crewId: string): string => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/?crew=${crewId}`;
};

// 크루 색상 생성 (이름 기반)
export const generateCrewColor = (name: string): string => {
  const colors = [
    '#ef4444', '#f97316', '#f59e0b', '#eab308',
    '#84cc16', '#22c55e', '#10b981', '#14b8a6',
    '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1',
    '#8b5cf6', '#a855f7', '#d946ef', '#ec4899'
  ];
  
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
};

// 크루 멤버 수 제한 확인
export const canJoinCrew = (crew: Crew): boolean => {
  return crew._count.members < crew.maxMembers;
};

// 크루 생성 가능 여부 확인 (사용자당 최대 3개)
export const canCreateCrew = (userCrews: Crew[]): boolean => {
  return userCrews.length < 3;
};

export default {
  getAvailableCrews,
  getMyCrew,
  getMyCrews,
  getCrewById,
  createCrew,
  updateCrew,
  deleteCrew,
  joinCrew,
  leaveCrew,
  getJoinRequests,
  getMemberMeditationToday,
  getMyCrewMembersToday,
  processJoinRequest,
  transferLeadership,
  removeMember,
  isCrewLeader,
  isCrewMember,
  getCrewMemberRole,
  getCrewShareLink,
  generateCrewColor,
  canJoinCrew,
  canCreateCrew,
};
